// Badge catalog + award logic — port of lib/models/badge_model.dart and BadgeService.
import { collection, doc, getDoc, getDocs, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase';
import { parseProgram } from './program';
import {
  comebackCount,
  computeStreakSnapshot,
  measuredMonthCount,
  nutritionStreakWeeks,
  perfectMonthCount,
  perfectWeekCount,
  waterGoalStreak,
  waterGoalWeeks,
} from './streak';

const WORKOUT = '#FF6D00';
const MEAL = '#00C896';
const MASTER = '#FFB300';
const BODY = '#7C4DFF';
const WATER = '#2E9BFF';

// `repeatMetric` marks badges that can be earned again: its value is the total
// times earned. Milestones ("log 10 meals") stay one-time.
export const BADGE_CATALOG = [
  { id: 'first_workout', icon: 'flag', color: WORKOUT, tone: 'workout', metric: 'workoutCount', threshold: 1 },
  { id: 'meal_10', icon: 'restaurant', color: MEAL, tone: 'meal', metric: 'mealCount', threshold: 10 },
  { id: 'workout_5', icon: 'fitness_center', color: WORKOUT, tone: 'workout', metric: 'workoutCount', threshold: 5 },
  { id: 'perfect_week', icon: 'event_available', color: WORKOUT, tone: 'workout', metric: 'perfectWeekCount', threshold: 1, repeatMetric: 'perfectWeekCount' },
  { id: 'nutrition_streak_7', icon: 'restaurant_menu', color: MEAL, tone: 'meal', metric: 'nutritionStreak', threshold: 7, repeatMetric: 'nutritionStreakWeeks' },
  { id: 'water_week', icon: 'water_drop', color: WATER, tone: 'water', metric: 'waterGoalStreak', threshold: 7, repeatMetric: 'waterGoalWeeks' },
  { id: 'monthly_measurement', icon: 'straighten', color: BODY, tone: 'body', metric: 'measuredMonthCount', threshold: 1, repeatMetric: 'measuredMonthCount' },
  { id: 'body_progress_5', icon: 'monitor_weight', color: BODY, tone: 'body', metric: 'bodyProgressCount', threshold: 5 },
  { id: 'comeback', icon: 'restart_alt', color: MASTER, tone: 'master', metric: 'comebackCount', threshold: 1, repeatMetric: 'comebackCount' },
  { id: 'meal_50', icon: 'restaurant_menu', color: MEAL, tone: 'meal', metric: 'mealCount', threshold: 50 },
  { id: 'workout_25', icon: 'military_tech', color: WORKOUT, tone: 'workout', metric: 'workoutCount', threshold: 25 },
  { id: 'perfect_month', icon: 'calendar_month', color: MASTER, tone: 'master', metric: 'perfectMonthCount', threshold: 1, repeatMetric: 'perfectMonthCount' },
  { id: 'meal_100', icon: 'local_dining', color: MEAL, tone: 'meal', metric: 'mealCount', threshold: 100 },
  { id: 'workout_50', icon: 'emoji_events', color: MASTER, tone: 'master', metric: 'workoutCount', threshold: 50 },
];

/** `bodyDays`/`waterDays` are `{ id, ...data }` docs. */
export function metricsFrom({ progressDays, nutritionDays, bodyDays, waterDays = [], program, userData }) {
  const snapshot = computeStreakSnapshot({ progressDays, program, nutritionDays, userData });
  const waterGoal = Number(userData?.waterGoalMl) > 0 ? Number(userData.waterGoalMl) : 2500;
  return {
    workoutCount: progressDays.filter((p) => p.completed).length,
    mealCount: nutritionDays.reduce((s, d) => s + (d.entries || []).length, 0),
    bodyProgressCount: bodyDays.length,
    perfectWeekCount: perfectWeekCount(progressDays, program),
    nutritionStreak: snapshot.nutritionStreakDays,
    nutritionStreakWeeks: nutritionStreakWeeks(nutritionDays, userData),
    measuredMonthCount: measuredMonthCount(bodyDays),
    waterGoalStreak: waterGoalStreak(waterDays, waterGoal),
    waterGoalWeeks: waterGoalWeeks(waterDays, waterGoal),
    comebackCount: comebackCount(progressDays, nutritionDays, userData),
    perfectMonthCount: perfectMonthCount(progressDays, program),
  };
}

/**
 * Recomputes every metric, writes newly-earned badge docs and re-earns of repeatable
 * ones. Returns [{ id, count }] for what this call earned (count > 1 = a repeat).
 */
export async function checkAndAwardBadges(uid) {
  const [earned, userSnap, progress, nutrition, body, water] = await Promise.all([
    getDocs(collection(db, 'users', uid, 'badges')),
    getDoc(doc(db, 'users', uid)),
    getDocs(collection(db, 'progress_workouts', uid, 'days')),
    getDocs(collection(db, 'users', uid, 'nutrition_days')),
    getDocs(collection(db, 'progress_body', uid, 'days')),
    getDocs(collection(db, 'users', uid, 'water_days')),
  ]);
  const userData = userSnap.data() || {};
  let program = null;
  if (userData.exerciseProgramID) {
    const p = await getDoc(doc(db, 'exercisePrograms', userData.exerciseProgramID));
    program = p.exists() ? parseProgram(p.data().program) : null;
  }
  const metrics = metricsFrom({
    progressDays: progress.docs.map((d) => ({ id: d.id, ...d.data() })),
    nutritionDays: nutrition.docs.map((d) => ({ id: d.id, ...d.data() })),
    bodyDays: body.docs.filter((d) => Object.keys(d.data()).length).map((d) => ({ id: d.id, ...d.data() })),
    waterDays: water.docs.map((d) => ({ id: d.id, ...d.data() })),
    program,
    userData,
  });
  const have = Object.fromEntries(earned.docs.map((d) => [d.id, d.data()]));
  const creates = [];
  const updates = [];
  BADGE_CATALOG.forEach((b) => {
    const existing = have[b.id];
    const times = b.repeatMetric ? Math.max(1, metrics[b.repeatMetric] || 0) : 1;
    if (!existing) {
      if ((metrics[b.metric] || 0) < b.threshold) return;
      creates.push({ id: b.id, count: 1, data: { earnedAt: serverTimestamp(), ...(b.repeatMetric ? { count: times } : {}) } });
      return;
    }
    if (!b.repeatMetric) return;
    if (typeof existing.count !== 'number') {
      // Earned before badges could repeat: adopt the history's tally silently.
      updates.push({ id: b.id, silent: true, data: { count: times } });
    } else if (times > existing.count) {
      // Only ever raised: a program switch re-scores past perfect weeks and can shrink the tally.
      updates.push({ id: b.id, count: times, data: { count: times, lastEarnedAt: serverTimestamp() } });
    }
  });
  // Separate commits so a rejected update (rules not yet deployed) can't sink first-time awards.
  const fresh = [];
  const commit = async (items, write) => {
    if (!items.length) return;
    const batch = writeBatch(db);
    items.forEach((x) => write(batch, doc(db, 'users', uid, 'badges', x.id), x.data));
    await batch.commit();
    const earnedNow = items.filter((x) => !x.silent).map(({ id, count }) => ({ id, count }));
    if (earnedNow.length) window.dispatchEvent(new CustomEvent('gm:badges-earned', { detail: earnedNow }));
    fresh.push(...earnedNow);
  };
  await commit(creates, (batch, ref, data) => batch.set(ref, data));
  await commit(updates, (batch, ref, data) => batch.update(ref, data));
  return fresh;
}
